/**
 * Settings > Secure your brand: the section's state machine (load phase, dialog, busy action,
 * notice). The reducer is PURE: no timers, no `Date`, no copy. Every load and action answer carries
 * the brand it was asked for, and an answer for any other brand is dropped, so a brand switch
 * mid-read or mid-action can never paint the previous brand's state. A load answer also carries its
 * read's id, and only the newest read's answer lands.
 *
 * The loading rules follow the area's lessons: a refetch that fails keeps the last good answer on
 * screen, and only a first read that fails is the failed state (the Load failed notice with Retry).
 * The silent refusals (`SILENT_CODES`, Decision 10) close the dialog with no notice; the section
 * refetches and the current state says the rest.
 */
import { SILENT_CODES, type ContractError, type ContractStatus } from './api/contract';

export type LoadPhase =
  /** No brand to ask about. */
  | { kind: 'idle' }
  /** A read in flight. `previous`: the status kept on screen during a refetch (null on a first read). */
  | { kind: 'loading'; previous: ContractStatus | null }
  | { kind: 'loaded'; status: ContractStatus }
  /** A FIRST read failed (there was no previous answer to keep). */
  | { kind: 'failed'; error: ContractError | null };

/** The popup is the names form only: a send that lands closes it, and the section's step cards say
 *  the rest (Document sent, step 1 done, step 3 in the spotlight). */
export type Dialog = { kind: 'closed' } | { kind: 'form'; mode: 'start' | 'restart' };

export type ActionKind = 'start' | 'restart' | 'resend' | 'download';

/** A refusal to show. `error` null: the service did not answer (the generic line). */
export type Notice = { error: ContractError | null };

export type SecureBrandState = {
  brandId: string | null;
  load: LoadPhase;
  /** The id of the newest read started for the brand: only its answer lands (0: none started yet). */
  read: number;
  dialog: Dialog;
  busy: ActionKind | null;
  notice: Notice | null;
  /** The contract on screen was sent again by a Resend in this visit (step 1 says "Contract sent again"). */
  sentAgain: boolean;
  /** The sends that landed in this visit (Start, Start over, Resend), counted up until a brand switch resets it: each
   *  new one replays step 1's sent beat, also a Start over straight after a send and a second Resend. */
  sends: number;
};

export type SecureBrandAction =
  | { type: 'brand'; brandId: string | null }
  /** `read`: the read's id, from a counter that only grows, so a newer read always has a larger id. */
  | { type: 'load-start'; brandId: string; read: number }
  | { type: 'load-ok'; brandId: string; read: number; status: ContractStatus }
  | { type: 'load-failed'; brandId: string; read: number; error: ContractError | null }
  | { type: 'open'; mode: 'start' | 'restart' }
  | { type: 'close' }
  | { type: 'action-start'; kind: ActionKind }
  /** `status` is the answer of Start, Start over and Resend; Download answers null. */
  | { type: 'action-ok'; kind: ActionKind; brandId: string; status: ContractStatus | null }
  | { type: 'action-failed'; kind: ActionKind; brandId: string; error: ContractError | null };

const CLOSED: Dialog = { kind: 'closed' };

export const initialState: SecureBrandState = {
  brandId: null,
  load: { kind: 'idle' },
  read: 0,
  dialog: CLOSED,
  busy: null,
  notice: null,
  sentAgain: false,
  sends: 0,
};

/** The status to render (loaded, or the one kept during a refetch), else null. */
export const shownStatus = (load: LoadPhase): ContractStatus | null =>
  load.kind === 'loaded' ? load.status : load.kind === 'loading' ? load.previous : null;

export const reducer = (state: SecureBrandState, action: SecureBrandAction): SecureBrandState => {
  switch (action.type) {
    case 'brand':
      if (action.brandId === state.brandId) return state;
      // A brand switch resets everything: nothing of the previous brand's state carries over.
      return {
        brandId: action.brandId,
        load: action.brandId ? { kind: 'loading', previous: null } : { kind: 'idle' },
        read: 0,
        dialog: CLOSED,
        busy: null,
        notice: null,
        sentAgain: false,
        sends: 0,
      };

    case 'load-start':
      // A read for this brand becomes the newest one, also while another is in flight: the re-read
      // after a failed action must not wait on a read asked before it. Mount and focus never start a
      // second read while one runs (the section skips them), so only that re-read lands here mid-read.
      if (action.brandId !== state.brandId) return state;
      return {
        ...state,
        load: { kind: 'loading', previous: shownStatus(state.load) },
        read: action.read,
        // A notice is cleared by a read that starts after it, never by one already in flight: the read
        // a window focus starts may answer after the click's refusal. The open form keeps its notice:
        // the refetch after a failed Start over must not wipe the error the dialog shows.
        notice: state.dialog.kind === 'form' ? state.notice : null,
      };

    case 'load-ok':
      // Only the newest read lands, and only while it still waits for its answer. An older read's
      // answer is dropped (a brand switched away and back, a re-read that replaced it). An action that
      // answers while the read is in flight moves the phase to `loaded` with a newer status, which the
      // read's older answer must not replace.
      if (action.brandId !== state.brandId || action.read !== state.read || state.load.kind !== 'loading') {
        return state;
      }
      return { ...state, load: { kind: 'loaded', status: action.status } };

    case 'load-failed': {
      if (action.brandId !== state.brandId || action.read !== state.read) return state;
      // A failed read keeps whatever status is on screen: the one kept during the refetch, or one an
      // action answered while the refetch was running. Only a read with nothing to keep is failed.
      const kept = shownStatus(state.load);
      if (kept !== null) return { ...state, load: { kind: 'loaded', status: kept } };
      return { ...state, load: { kind: 'failed', error: action.error } };
    }

    case 'open':
      if (state.busy !== null) return state;
      return { ...state, dialog: { kind: 'form', mode: action.mode }, notice: null };

    case 'close':
      // Escape, the backdrop and Cancel cannot drop a send that is on its way.
      if (state.busy === 'start' || state.busy === 'restart') return state;
      return { ...state, dialog: CLOSED, notice: null };

    case 'action-start':
      return { ...state, busy: action.kind, notice: null };

    case 'action-ok':
      if (action.brandId !== state.brandId) return state;
      if (action.kind === 'download' || action.status === null) return { ...state, busy: null };
      // The send landed: the popup closes and the status it answered goes on screen. A Start or a
      // Start over is a new contract (sent once); a Resend sends the same one again.
      return {
        ...state,
        busy: null,
        load: { kind: 'loaded', status: action.status },
        dialog: CLOSED,
        sentAgain: action.kind === 'resend',
        sends: state.sends + 1,
      };

    case 'action-failed':
      if (action.brandId !== state.brandId) return state;
      if (action.error && SILENT_CODES.has(action.error.code)) {
        return { ...state, busy: null, dialog: CLOSED, notice: null };
      }
      return { ...state, busy: null, notice: { error: action.error } };

    default:
      return state;
  }
};

/** The state for a brand before its first read: `useReducer`'s lazy initialiser, so the first paint
 *  is already loading for the brand and never shows the idle "no brand" layout. */
export const initState = (brandId: string | null): SecureBrandState =>
  reducer(initialState, { type: 'brand', brandId });

/** Skeleton: a brand is coming (`brandSettled` false), the reducer's brand lags the store's, or the
 *  first read is in flight. A refetch keeps the status on screen instead. */
export const showsSkeleton = (state: SecureBrandState, storeBrandId: string | null, brandSettled: boolean): boolean =>
  !brandSettled || state.brandId !== storeBrandId || (state.load.kind === 'loading' && state.load.previous === null);
